import { TIME_DATE_FORMAT } from "constants"
import dayjs from "dayjs"
import { capitalizeFirstLetter, convertToReadableString } from "utils"
import { t } from "i18next"
import { Number } from "components/common"
import { customDateFormat, dateTimeFunction } from "utils/date"

const getUserData = (item, parentId) => {
  let obj
  if (item?.data) {
    obj = {
      ...item.data,
      id: item.data.userId,
      name: item.data.userName,
      parentId: parentId,
      joiningDate: customDateFormat(dateTimeFunction({ date: item?.data?.joiningDate, Default: true })),
      isLineManager: !!item?.children?.length,
      ...(item?.children && { children: item.children.map(e => getUserData(e, parentId)) }),
      isTelesales: item?.data?.designation === "TSR" || item?.data?.designation === "TSRAFF",
    }
  } else {
    obj = {
      ...item,
      type: "region",
      id: item?.cities ? "region-" + item?.regionId : item?.cityId,
      name: item?.cityName || item.regionName,
      parentId: parentId,
      ...(item?.cities && {
        children: item.cities.map(e => getUserData(e, item?.cities ? "region-" + item?.regionId : item?.cityId)),
      }),
    }
  }
  return obj
}

const UpdateCallKeys = (obj, parentKey = "") => {
  const transformKey = (obj, key, parentKey) => {
    const newKey = key + capitalizeFirstLetter(parentKey)
    obj[newKey] = obj[key]
    delete obj[key]
  }
  for (let key in obj) {
    if (typeof obj[key] === "object" && obj[key] !== null) {
      if (key === "averageScore") {
        transformKey(obj, key, parentKey)
      }
      UpdateCallKeys(obj[key], key)
    } else if (key === "noOfCalls" || key === "averageScore") {
      transformKey(obj, key, parentKey)
    }
  }
}
export const analyticsTransformer = response => {
  let listKeys = []
  Object.keys(response?.comparison?.staticKeys).forEach((item, index) => {
    listKeys.push({
      key: item,
      label: t(capitalizeFirstLetter(item)),
      title: t(capitalizeFirstLetter(item)),
      children: [],
    })
    Object.keys(response?.comparison?.staticKeys[item]).forEach(e => {
      listKeys[index].children.push({
        ...response?.comparison?.staticKeys[item][e],
        key: e,
        parent: item,
      })
    })
  })

  return {
    ...response,
    listKeys,
  }
}

export const userKpiTransformer = response => {
  let listKeys = []

  const parentKeys = Object.keys(response?.userKpi?.staticKeys || {})
    .map(parentKey => ({
      parentKey,
      order: response.userKpi.staticKeys[parentKey]?.order ?? 0,
    }))
    .sort((a, b) => a.order - b.order)

  parentKeys.map(({ parentKey }) => {
    const children = Object.keys(response?.userKpi?.staticKeys[parentKey] || {})
      .filter(childKey => childKey !== "order") // ignore order key
      .map(childKey => {
        const { order, ...childData } = response.userKpi.staticKeys[parentKey][childKey] || {}
        return {
          type: childData?.type ? childData.type : childData?.hover ? "hover" : "number",
          ...childData,
          key:
            childKey === "noOfCalls" || childKey === "averageScore"
              ? childKey + capitalizeFirstLetter(parentKey)
              : childKey,
          slopeKey: `${childKey}Slope`,
          parent: parentKey,
          hoverKeys: childData?.hover ? childData?.hoverKeys : {},
          hasDrawer: childData?.hasDrawer,
          order: order ?? 0,
        }
      })
      .sort((a, b) => a.order - b.order)
    listKeys.push({
      key: parentKey,
      label: t(capitalizeFirstLetter(parentKey)),
      title: t(capitalizeFirstLetter(parentKey)),
      children,
    })
  })

  const userKpi = {
    ...response.userKpi,
    data: response.userKpi.data.map(item => getUserData(item, item?.data?.userId || item?.regionId)),
  }
  UpdateCallKeys(userKpi.data)
  return { ...response, listKeys, userKpi }
}

export const projectWiseRevenueTransformer = response => {
  let totalCount = 0
  const data = Object.keys(response?.inspection_wise_calls?.inspectionWiseCalls)?.map(key => {
    const type = response?.staticKeys[key]?.label || ""
    const value = response?.inspection_wise_calls?.inspectionWiseCalls[key] || 0
    totalCount += value
    return {
      type: type,
      value: value,
    }
  })

  const colors = ["#26B0FF", "#6478DE", "#4777F2", "#47B0CA"]

  return {
    inspection_wise_calls: {
      data: data,
      colors: colors,
      total: totalCount,
    },
  }
}

export const cardsDataTransformer = response => {
  const transformedResponse = {}

  Object.keys(response).map(cardKey => {
    if (cardKey != "teamTypes") {
      const cardInfo = response[cardKey]
      let cardData = {}

      Object.keys(cardInfo).map(key => {
        if (typeof cardInfo[key] == "object") {
          const staticKeyObj = cardInfo[key]
          cardData = { ...cardData, ...staticKeyObj[Object.keys(staticKeyObj)[0]] }
        } else {
          cardData.count = cardInfo[Object.keys(cardInfo)[0]]
        }
      })

      //Red color data for Fake Calls Only
      if (cardKey == "fake_calls") {
        cardData.color = "red"
      }
      transformedResponse[cardKey] = cardData
    }
  })

  return transformedResponse
}

export const callScoringBreakdownTransformer = response => {
  const inspectionScores = response?.inspection_scoring_breakdown?.inspectionScoringBreakdown
  const scoringStandards = response?.inspection_scoring_breakdown?.staticKeys?.inspectionScoringBreakdown
  const transformedData = {}
  const tabList = []

  Object?.keys(inspectionScores)?.map(key => {
    const tab = {
      key: key,
      tab: convertToReadableString(key),
    }

    if (scoringStandards?.hasOwnProperty(key)) {
      const currentInspectionScore = inspectionScores?.[key]
      const currentScoringStandard = scoringStandards?.[key]

      const scoreBreakdown = []

      Object?.keys(currentInspectionScore)?.forEach(innerKey => {
        if (
          innerKey != "averageRegionalScore" &&
          innerKey != "averageTotalScore" &&
          currentScoringStandard?.hasOwnProperty(innerKey)
        ) {
          scoreBreakdown.push({
            value: <Number value={currentInspectionScore?.[innerKey]} />,
            label: currentScoringStandard?.[innerKey]?.label,
            totalValue: currentScoringStandard[innerKey].total,
          })
        }
      })

      const ai_insights = {
        regional_average: currentInspectionScore?.averageRegionalScore
          ? parseFloat(currentInspectionScore?.averageRegionalScore?.toFixed(1))
          : 0,
        score: currentInspectionScore?.averageTotalScore
          ? parseFloat(currentInspectionScore?.averageTotalScore?.toFixed(1))
          : 0,
        total: currentScoringStandard?.averageTotalScore?.total
          ? parseFloat(currentScoringStandard?.averageTotalScore?.total?.toFixed(1))
          : 0,
      }

      const tabData = {
        scoreBreakdown: scoreBreakdown,
        ai_insights: ai_insights,
      }

      tabList.push(tab)
      transformedData[key] = tabData
    }
  })

  transformedData.tabList = tabList
  return transformedData
}
